import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Search, UserPlus, Send, ChevronDown } from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfileContext } from '../context/UserProfileContext';
import { searchUsers, sendPeerFeedbackRequest } from '../utils/peerFeedbackApi';
import { UserSearchResult } from '../types/peerFeedback';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  /** Lower-cased emails of recipients with an open (pending) request. New requests to these emails are blocked. */
  pendingRecipientEmails?: Set<string>;
  /** Called when the user tries to add/submit a recipient that already has an open request. */
  onDuplicateRecipient?: (displayName: string) => void;
}

export function RequestFeedbackModal({
  onClose,
  onSuccess,
  pendingRecipientEmails,
  onDuplicateRecipient,
}: Props) {
  const { apiCall } = useApiClient();
  const { userProfile } = useUserProfileContext();

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selected, setSelected] = useState<UserSearchResult[]>([]);
  const [message, setMessage] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsers(query, apiCall);
        // Exclude already selected
        const selectedEmails = new Set(selected.map((s) => s.email));
        setSearchResults(results.filter((r) => !selectedEmails.has(r.email)));
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, selected, apiCall]);

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((user: UserSearchResult) => {
    if (pendingRecipientEmails?.has(user.email.toLowerCase())) {
      onDuplicateRecipient?.(user.displayName);
      setQuery('');
      setShowDropdown(false);
      setSearchResults([]);
      return;
    }
    setSelected((prev) => [...prev, user]);
    setQuery('');
    setShowDropdown(false);
    setSearchResults([]);
    searchRef.current?.focus();
  }, [pendingRecipientEmails, onDuplicateRecipient]);

  const handleRemove = useCallback((email: string) => {
    setSelected((prev) => prev.filter((s) => s.email !== email));
  }, []);

  const handleSubmit = async () => {
    if (!userProfile || selected.length === 0) return;
    // Final guard: if any selected recipient already has an open request, block and notify.
    if (pendingRecipientEmails) {
      const dup = selected.find((s) => pendingRecipientEmails.has(s.email.toLowerCase()));
      if (dup) {
        onDuplicateRecipient?.(dup.displayName);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      await sendPeerFeedbackRequest(
        {
          requesterId: userProfile.employeeId,
          requesterName: userProfile.displayName,
          requesterEmail: userProfile.email,
          recipients: selected.map((s) => ({
            employeeId: s.employeeId,
            displayName: s.displayName,
            email: s.email,
          })),
          message: message.trim() || undefined,
        },
        apiCall
      );
      onSuccess();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-pink-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Request Feedback</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Recipient search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Who do you want feedback from?
            </label>

            {/* Selected chips */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selected.map((s) => (
                  <span
                    key={s.email}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-50 border border-pink-200 text-pink-700 text-sm"
                  >
                    {s.displayName}
                    <button
                      onClick={() => handleRemove(s.email)}
                      className="text-pink-400 hover:text-pink-700 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search input + dropdown */}
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:border-pink-500 transition-colors"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Dropdown results */}
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-52 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.email}
                      onClick={() => handleSelect(user)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{user.displayName}</div>
                        <div className="text-xs text-slate-500">{user.jobTitle || user.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showDropdown && !searching && searchResults.length === 0 && query.length >= 2 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-500">
                  No users found for "{query}"
                </div>
              )}
            </div>
          </div>

          {/* Optional message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Personal note <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add context about what you'd like feedback on…"
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:border-pink-500 transition-colors resize-none"
            />
            <div className="text-xs text-slate-400 text-right mt-1">{message.length}/500</div>
          </div>

          {/* The 3 questions preview */}
          <div className="rounded-xl bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100 p-4">
            <p className="text-xs font-bold text-pink-500 uppercase tracking-widest mb-3">
              Questions that will be asked
            </p>
            <div className="space-y-2.5">
              {[
                'What is one thing I should start, stop, and continue doing to make our collaboration more effective?',
                "When I'm managing a high-pressure situation or a complex project, how does my leadership/working style affect the team's momentum?",
                'What should I do more to help the team succeed?',
              ].map((q, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-pink-100 text-pink-600 text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-slate-700 leading-snug">{q}</p>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={selected.length === 0 || submitting}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium bg-pink-600 hover:bg-pink-500 disabled:bg-slate-600 disabled:text-slate-400 text-white rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
            {submitting
              ? 'Sending…'
              : `Send Request${selected.length > 1 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
